import React, { useRef, useState } from 'react';
import type { ViewerBrandConfig } from '../../../server/admin/viewer-config/types';
import { authenticatedFetch } from '../../../lib/apiClient';

interface BrandConfigTabProps {
  value: ViewerBrandConfig;
  onChange: (updated: ViewerBrandConfig) => void;
  companyId?: string;
  productId?: string;
}

const FONT_OPTIONS = [
  'Arial, sans-serif',
  'Inter, sans-serif',
  'Roboto, sans-serif',
  'system-ui, sans-serif',
  'Georgia, serif',
];

export function BrandConfigTab({ value, onChange, companyId, productId }: BrandConfigTabProps) {
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);

  const update = (field: keyof ViewerBrandConfig, val: string) => {
    onChange({ ...value, [field]: val });
  };

  const handleLogoUpload = async (file: File) => {
    if (!companyId || !productId) return;
    setIsUploadingLogo(true);
    try {
      const intentRes = await authenticatedFetch(
        `/api/admin/viewer-projects/${companyId}/${productId}/upload-file`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fileName: file.name,
            contentType: file.type,
            sizeBytes: file.size,
            purpose: 'brand-logo',
          }),
        },
      );
      const intentJson = await intentRes.json();
      if (!intentRes.ok) throw new Error(intentJson.error?.message ?? 'Upload failed');

      // Upload to S3
      const uploadRes = await fetch(intentJson.data.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!uploadRes.ok) throw new Error('Logo upload to storage failed');

      // Auto-populate logoUrl
      update('logoUrl', intentJson.data.cdnUrl);
    } catch (err) {
      console.error('Logo upload failed:', err);
    } finally {
      setIsUploadingLogo(false);
    }
  };

  return (
    <div>
      <div className="vc-field">
        <label className="vc-label">Company Name</label>
        <input
          className="vc-input"
          type="text"
          value={value.companyName || ''}
          onChange={(e) => update('companyName', e.target.value)}
          placeholder="e.g. Weijia Construction"
        />
      </div>

      <div className="vc-field">
        <label className="vc-label">Product Name</label>
        <input
          className="vc-input"
          type="text"
          value={value.productName || ''}
          onChange={(e) => update('productName', e.target.value)}
          placeholder="e.g. Sunshade Louver Pergola"
        />
      </div>

      <div className="vc-field">
        <label className="vc-label">Accent Color</label>
        <div className="vc-color-field">
          <input
            type="color"
            className="vc-color-swatch"
            value={value.accentColor || '#38bdf8'}
            onChange={(e) => update('accentColor', e.target.value)}
          />
          <input
            className="vc-input vc-color-input"
            type="text"
            value={value.accentColor || ''}
            onChange={(e) => update('accentColor', e.target.value)}
            placeholder="#38bdf8"
          />
        </div>
      </div>

      <div className="vc-field">
        <label className="vc-label">Header Background Color</label>
        <div className="vc-color-field">
          <input
            type="color"
            className="vc-color-swatch"
            value={value.headerBackgroundColor || '#373737'}
            onChange={(e) => update('headerBackgroundColor', e.target.value)}
          />
          <input
            className="vc-input vc-color-input"
            type="text"
            value={value.headerBackgroundColor || ''}
            onChange={(e) => update('headerBackgroundColor', e.target.value)}
            placeholder="rgba(55, 55, 55, 0.85)"
          />
        </div>
      </div>

      <div className="vc-field">
        <label className="vc-label">Font Family</label>
        <select
          className="vc-select"
          value={value.fontFamily || 'Arial, sans-serif'}
          onChange={(e) => update('fontFamily', e.target.value)}
        >
          {FONT_OPTIONS.map((font) => (
            <option key={font} value={font}>{font.split(',')[0]}</option>
          ))}
        </select>
      </div>

      <div className="vc-field">
        <label className="vc-label">Logo URL</label>
        <input
          className="vc-input"
          type="url"
          value={value.logoUrl || ''}
          onChange={(e) => update('logoUrl', e.target.value)}
          placeholder="https://..."
        />
        {companyId && productId && (
          <div style={{ marginTop: '0.4rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button
              type="button"
              className="vc-add-btn"
              style={{ margin: 0, fontSize: '0.7rem', padding: '0.3rem 0.6rem' }}
              onClick={() => logoInputRef.current?.click()}
              disabled={isUploadingLogo}
            >
              {isUploadingLogo ? 'Uploading...' : '↑ Upload Logo'}
            </button>
            <input
              ref={logoInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.webp"
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleLogoUpload(f);
                e.target.value = '';
              }}
            />
          </div>
        )}
        {value.logoUrl && (
          <div style={{ marginTop: '0.5rem' }}>
            <img
              src={value.logoUrl}
              alt="Logo preview"
              style={{
                maxHeight: '2.5rem',
                maxWidth: '8rem',
                objectFit: 'contain',
                borderRadius: '0.25rem',
                border: '1px solid rgba(255,255,255,0.1)',
              }}
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
